import type { RfqReleasePurpose } from '../types';

type PurposeCarrier = { releasePurpose?: RfqReleasePurpose | string | null } | null | undefined;

export function parseRfqReleasePurpose(raw: unknown): RfqReleasePurpose {
  return raw === 'reference' ? 'reference' : 'quote';
}

/** Reference releases / packages are shared for production, never priced. */
export function isReferenceRelease(row: PurposeCarrier): boolean {
  return parseRfqReleasePurpose(row?.releasePurpose) === 'reference';
}

export const RFQ_RELEASE_PURPOSE_LABELS: Record<RfqReleasePurpose, string> = {
  quote: 'Quoting release',
  reference: 'Reference release',
};

export const RFQ_RELEASE_PURPOSE_HINTS: Record<RfqReleasePurpose, string> = {
  quote: 'Suppliers are asked to price this version.',
  reference:
    'Refined model for a quote that is already approved. Suppliers cut from this version — they do not requote it.',
};

/** Short chip copy for a release's structure scope. Empty scope = whole project. */
export function releaseStructureScopeLabel(
  structureNames: ReadonlyArray<string> | null | undefined,
  totalStructureCount?: number,
): string {
  const names = (structureNames ?? []).map((n) => (n || '').trim()).filter(Boolean);
  if (names.length === 0) return 'All structures';
  if (totalStructureCount && totalStructureCount > 0 && names.length >= totalStructureCount) {
    return 'All structures';
  }
  return names.join(', ');
}

/** Compact count chip, e.g. "2 of 9 structures". */
export function releaseStructureCountLabel(
  structureNames: ReadonlyArray<string> | null | undefined,
  totalStructureCount?: number,
): string | null {
  const count = (structureNames ?? []).filter((n) => (n || '').trim()).length;
  if (count === 0) return null;
  if (totalStructureCount && totalStructureCount > 0) {
    if (count >= totalStructureCount) return null;
    return `${count} of ${totalStructureCount} structures`;
  }
  return `${count} structure${count === 1 ? '' : 's'}`;
}

/**
 * Starting checks for the staging dialog. Ids the proposal still has win.
 * When the track only has names, match those. Unknown tags are dropped.
 * An empty result means the whole project.
 */
export function seedStagingStructureIds(
  structures: ReadonlyArray<{ id: string; name: string }>,
  structureIds?: ReadonlyArray<string> | null,
  structureNames?: ReadonlyArray<string> | null,
): string[] {
  const known = new Set(structures.map((s) => s.id));
  const fromIds: string[] = [];
  const seen = new Set<string>();
  for (const raw of structureIds ?? []) {
    const id = (raw || '').trim();
    if (!id || !known.has(id) || seen.has(id)) continue;
    seen.add(id);
    fromIds.push(id);
  }
  if (fromIds.length > 0) return fromIds;

  const byName = new Map<string, string>();
  for (const structure of structures) {
    const key = structure.name.trim().toLowerCase();
    if (key && !byName.has(key)) byName.set(key, structure.id);
  }
  const fromNames: string[] = [];
  for (const raw of structureNames ?? []) {
    const id = byName.get((raw || '').trim().toLowerCase());
    if (!id || seen.has(id)) continue;
    seen.add(id);
    fromNames.push(id);
  }
  return fromNames;
}
